'use strict'

import LZString from "lz-string";

/*
 * WebSockets wrapper class.
 */
class WsClient {
    /**
     * Constructor
     * @param {AppStore} appStore
     * @param {string} wsServerUrl The base base URL of the WebSockets server
     * @param {function} openCallbackFunc An optional function to call on websocket 'open' events.
     */
    constructor(appStore, wsServerUrl, openCallbackFunc) {
        this.appStore = appStore;
        this.logger = this.appStore.getLogger();
        // Pass the app name in on a request parameter. The WS handler needs it to register the connection.
        this.wsServerUrl = `${wsServerUrl}?appName=${this.appStore.getApplicationName()}`;
        this.openCallbackFunc = openCallbackFunc;

        // Send the sessionId as the subprotocol for authentication. If the session turns out to be invalid the
        // WS connection will fail ...
        this.socket = new WebSocket(this.wsServerUrl, [appStore.getSessionId()]);
        // Another way to convey info to the WS API during connection is ...
        //this.socket = new WebSocket(this.wsServerUrl + "?foo=bar");

        // Storage for message callbacks
        this.callbacks = {};
        // Handle for the keep-alive timer function
        this.pingTimerFunc;

        /*
         * Register for WebSocket events ...
         */
        // If there's a callback then configure a timeout in case the connection fails.
        let wsConnectionTimeoutTimer;
        if (this.openCallbackFunc) {
            wsConnectionTimeoutTimer = setTimeout(() => {
                this.logger.error("Timeout for establishing a websocket connection exceeded");
                this.openCallbackFunc();
            }, this.appStore.getWebSocketsTimeout());
        }
        // When a connection is established this method is called ...
        this.socket.addEventListener("open", (event) => {
            this.logger.debug("Got WebSocket open event.");
            // Clear the timeout ...
            window.clearTimeout(wsConnectionTimeoutTimer);
            // Set a timer to send a request to the WS API at a configured interval ...
            this.pingTimerFunc = window.setInterval(() => {
                this.logger.debug("Sending ping");
                this.socket.send('{"action": "ping"}');
                // The socket will timeout after 10 minutes of inactivity, so send some traffic after 9 minutes.
                // (That's a hard AWS limit).
            }, 540000);

            // Call the open callback function if one was provided.
            if (this.openCallbackFunc) {
                this.openCallbackFunc();
            }
        });

        // Handler for messages from the WebSockets server ...
        this.socket.addEventListener("message", (event) => {
            this.logger.debug("Got websockets message event");
            this.receive(event);
        });

        // Errors ...
        this.socket.addEventListener("error", (event) => {
            this.logger.error(`Got websocket error event: ${JSON.stringify(event)}`);
        });

        // Socket closed ...
        this.socket.addEventListener("close", (event) => {
            this.logger.info(`Got websocket close event: ${JSON.stringify(event)}`);
            window.clearTimeout(this.pingTimerFunc);
        });
    }

    /**
     * Sends a message to the WebSocket server...
     * @public
     */
    send(data) {
        // Send a message upstream
        this.socket.send(data);
    }

    /**
     * Handles incoming messages
     * @private
     */
    receive(event) {
        if (! event.data) {
            // Not empty strings.
            return;
        }

        let message;
        try {
            message = JSON.parse(event.data);
        } catch (e) {
            this.logger.error(`WebSocket message was not in JSON format: [${event.data}]`);
            return;
        }

        // See if this is a keep-alive response
        if (message.ping) {
            if (message.ping === "pong") {
                this.logger.debug("Got pong!");
            } else {
                this.logger.error("Got a ping with no pong.");
            }
        } else if (message.callbackEventName) {
            // Otherwise, process the message as an async response ....
            const callbackEventName = message.callbackEventName
            if (callbackEventName) {
                if (this.callbacks[callbackEventName]) {
                    // Call the callback with the decoded result
                    this.callbacks[callbackEventName](JSON.parse(LZString.decompressFromBase64(message.response)));
                } else {
                    this.logger.error(`No callback was registered for event [${callbackEventName}]`);
                }
            }
        } else {
            this.logger.error(`Unknown message type from WS API [${event.data}]`);
        }
    }

    /**
     * Register a callback.
     * @param callbackName {string} The name of the callback.
     * @param callbackFunc {function} The function to call.
     * @public
     * @returns {undefined}
     */
    register(callbackName, callbackFunc) {
        this.callbacks[callbackName] = callbackFunc;
    }

    /**
     * Unregister a callback.
     * @param callbackName {name} The name of the callback.
     */
    remove(callbackName) {
        delete this.callbacks[callbackName];
    }

    /**
     * Closes the WebSocket connection.
     * @private
     */
    close() {
        this.socket.close();
    }

    /**
     * Check the state of the socket.
     * @returns {boolean} True if websockets is enabled in the app and a socket is currently open. Otherwise, false.
     */
    isWebSocketsActive() {
        return this.appStore.isWebSocketsEnabled()
            && this.socket.readyState === WebSocket.OPEN;
    }
}
export default WsClient;